import { Node, Edge, Position } from 'reactflow'; // Added Position
import { ClusterEnvironmentConfig, ModuleConfig } from './cluster';
import { PlaybookNodeData } from '../store/usePlaybookStore'; // Import PlaybookNodeData
export * from './integration'; // Export integration types
export * from './report'; // Export report types

export type { Node, Edge, Position } from 'reactflow'; // Re-export Node, Edge AND Position
export * from './cluster'; // Export all cluster-related types

// Define possible handle types and positions
export type HandleType = 'source' | 'target';
export type HandlePosition = Position.Left | Position.Right | Position.Top | Position.Bottom;

// Define the structure for a single handle configuration
export interface HandleConfig {
  id: string; // Unique identifier for the handle (e.g., uuid)
  type: HandleType;
  position: HandlePosition;
  color: string; // Tailwind color class name (e.g., 'bg-blue-500')
  // Add any other handle-specific properties if needed in the future
}

// Add 'mapping' to NodeType for legacy support
export type NodeType = 'source' | 'javascript-source' | 'mobile-sdk' | 'server-source' | 'destination' | 'cluster' | 'pipeline' | 'sync-injector' | 'load-balancer' | 'transformations' | 'enrichment' | 'lua' | 'spreading' | 'filter' | 'schema-enforcement' | 'bot-filtering' | 'data-replay' | 'event' | 'text-node' | 'notes-node' | 'group' | 'mapping';
export type SourceType = 'javascript' | 'android' | 'mobile-sdk' | 'server' | 'api';
export type DestinationType = 'integration' | 'pipeline' | 'sync-injector' | 'load-balancer' | 'playbook' | 'custom';

export interface BaseNodeData {
  label: string;
  name?: string;
  type: string;
  description?: string;
  documentation?: string;
  project: string;
  sources?: string[];
  targets?: string[];
  status?: 'active' | 'inactive' | 'connected' | 'pending' | 'error' | string;
  onClose?: (id: string) => void;
  onDelete?: (id: string) => void;
  onUpdate?: (updatedData: Partial<AnyNodeData>) => void;
  handles?: HandleConfig[]; // Add handles property to BaseNodeData
}

export interface SourceNodeData extends BaseNodeData {
  type: 'source';
  subType: SourceType;
  url?: string;
  eventVolume?: string;
  syncInjectorId?: string;
  eventSpec?: string;
  consentTool?: string;
  otherConsentToolName?: string;
  latencyImpact?: number | string | null;
  // Visibility settings for node labels
  urlVisibility?: "hidden" | "visible";
  eventVolumeVisibility?: "hidden" | "visible";
  eventSpecVisibility?: "hidden" | "visible";
  consentToolVisibility?: "hidden" | "visible";
  latencyImpactVisibility?: "hidden" | "visible";
  // Notes field and visibility
  notes?: string;
  notesVisibility?: "hidden" | "visible";
  // Optional handles for dynamic handle support
  handles?: HandleConfig[];
}

export interface DestinationNodeData extends BaseNodeData {
  type: 'destination';
  icon?: string;
  category?: string;
  docs?: string | string[] | Array<{ label: string, url: string }> | { links: Array<{ label: string, url: string }> };
  schema?: string;
  latencyImpact?: number | string | null;
  // Custom fields
  customLabel?: string;
  teamAccess?: string;
  otherTeamAccess?: string;
  // Visibility settings
  statusVisibility?: "hidden" | "visible";
  customLabelVisibility?: "hidden" | "visible";
  teamAccessVisibility?: "hidden" | "visible";
  latencyImpactVisibility?: "hidden" | "visible"; // Added latency impact visibility
  // Notes field and visibility
  notes?: string;
  notesVisibility?: "hidden" | "visible";

  // Add the new handles array - Initialize as empty or with defaults later
  handles?: HandleConfig[];
}

export interface PipelineNodeData extends BaseNodeData {
  type: 'pipeline';
  handles?: HandleConfig[];
  writekey?: string;
  modules?: ModuleConfig[];
  idSyncs?: string[];
  name?: string;
  notes?: string;
  // Visibility settings
  nameVisibility?: "hidden" | "visible";
  labelVisibility?: "hidden" | "visible";
  writeKeyVisibility?: "hidden" | "visible";
  notesVisibility?: "hidden" | "visible";
}

export interface LoadBalancerNodeData extends BaseNodeData {
  type: 'load-balancer';
  strategy?: string;
  customLabel?: string;
  statusVisibility?: "hidden" | "visible";
  strategyVisibility?: "hidden" | "visible";
  customLabelVisibility?: "hidden" | "visible";
  handles?: HandleConfig[]; // Add handles property
}

export interface ProcessorNodeData extends BaseNodeData {
  type: 'processor';
}

export interface SinkNodeData extends BaseNodeData {
    type: 'sink';
}

export interface MappingNodeData extends BaseNodeData {
  type: 'processor';
  nodeType: 'mapping';
  handles?: HandleConfig[];
  transformationType?: string;
  inputKey?: string;
  outputKey?: string;
  name?: string;
  notes?: string;
  category?: string;
  categoryVisibility?: "hidden" | "visible";
  transformationTypeVisibility?: "hidden" | "visible";
  // Visibility settings
  nameVisibility?: "hidden" | "visible";
  labelVisibility?: "hidden" | "visible";
  notesVisibility?: "hidden" | "visible";
}

export interface FilterNodeData extends BaseNodeData {
  type: 'filter';
  handles?: HandleConfig[];
  filterType?: string;
  name?: string;
  notes?: string;
  // Visibility settings
  nameVisibility?: "hidden" | "visible";
  filterTypeVisibility?: "hidden" | "visible";
  notesVisibility?: "hidden" | "visible";
}

export interface EnrichmentNodeData extends BaseNodeData {
  type: 'processor';
  nodeType: 'enrichment';
  enrichmentType?: string;
}

export interface LuaNodeData extends BaseNodeData {
  type: 'processor';
  nodeType: 'lua';
  expression?: string;
  // Team access fields
  teamAccess?: string;
  otherTeamAccess?: string;
  teamAccessVisibility?: "hidden" | "visible";
}

export interface SpreadingNodeData extends BaseNodeData {
  type: 'processor';
  nodeType: 'spreading';
  inputKey?: string;
  events?: string;
}



export interface SchemaEnforcementNodeData extends BaseNodeData {
  type: 'processor';
  nodeType: 'schema';
  handles?: HandleConfig[];
  invalidEventsAction?: string;
  events?: string;
  name?: string;
  notes?: string;
  // Visibility settings
  nameVisibility?: "hidden" | "visible";
  notesVisibility?: "hidden" | "visible";
}

export interface EventNodeData extends BaseNodeData {
  type: 'event';
  handles?: HandleConfig[];
  notes?: string;
  // Visibility settings
  nameVisibility?: "hidden" | "visible";
  labelVisibility?: "hidden" | "visible";
  notesVisibility?: "hidden" | "visible";
}

export interface SyncInjectorNodeData extends BaseNodeData {
  type: 'sync-injector';
  domains: string[];
  crossDomain?: boolean;
  idSyncs?: string[];
  customLabel?: string;
  idSyncsVisibility?: "hidden" | "visible";
  crossDomainVisibility?: "hidden" | "visible";
  customLabelVisibility?: "hidden" | "visible";
  handles?: HandleConfig[]; // Add handles property
}

export interface ClusterNodeData extends BaseNodeData {
  type: 'cluster';
  clusterEnvironment: ClusterEnvironmentConfig;
  width?: number;
  modules: ModuleConfig[];
  idSyncs?: string[];
  totalLatencyImpact?: number | null;
  onAddSource?: (type: string, position: { x: number; y: number }) => void;
  onAddDestination?: (type: string, position: { x: number; y: number }) => void;
  isAdvancedMode?: boolean;
  // Custom fields
  teamAccess?: string;
  otherTeamAccess?: string;
  customLabel?: string;
  // Visibility settings
  environmentVisibility?: "hidden" | "visible";
  teamAccessVisibility?: "hidden" | "visible";
  customLabelVisibility?: "hidden" | "visible";
  moduleVisibility?: "hidden" | "visible";
  handles?: HandleConfig[]; // Add handles property
}

export interface BotFilteringNodeData extends BaseNodeData {
  type: 'processor';
  nodeType: 'bot-filtering';
  handles?: HandleConfig[];
  filteringMethod?: string;
  botDefinition?: string;
  name?: string;
  notes?: string;
  // Visibility settings
  nameVisibility?: "hidden" | "visible";
  notesVisibility?: "hidden" | "visible";
}

export interface DataReplayNodeData extends BaseNodeData {
  type: 'processor';
  nodeType: 'data-replay';
  handles?: HandleConfig[];
  replaySource?: string;
  name?: string;
  notes?: string;
  // Visibility settings
  nameVisibility?: "hidden" | "visible";
  notesVisibility?: "hidden" | "visible";
  replayVisibility?: "hidden" | "visible";
}

export interface GroupNodeData extends BaseNodeData {
  type: 'group';
  name: string;
  color?: string; // Made color optional to match src/node-types/group-node/types.ts
  icon: string;
  hasIncomingHandle: boolean;
  hasOutgoingHandle: boolean;
  allowedNodeTypes: string[];
  customLabel: string;
  groupNameVisibility: "hidden" | "visible";
  iconVisibility: "hidden" | "visible";
  customLabelVisibility: "hidden" | "visible";
}

export interface TextNodeData extends BaseNodeData {
  type: 'text-node';
  content?: string; // Stores serialized Lexical editor state (JSON string)
  // textStyle and textColor are now handled by the rich text editor content
  // Visibility settings
  contentVisibility?: "hidden" | "visible";
}

export interface NotesNodeData extends BaseNodeData {
  type: 'notes-node';
  name?: string;
  notes?: string;
  color?: string;
  // Visibility settings
  nameVisibility?: "hidden" | "visible";
  notesVisibility?: "hidden" | "visible";
}

// Add PlaybookNodeData to the union
export type AnyNodeData = SourceNodeData | DestinationNodeData | ClusterNodeData | SyncInjectorNodeData | PipelineNodeData | LoadBalancerNodeData | ProcessorNodeData | SinkNodeData | MappingNodeData | EnrichmentNodeData | LuaNodeData | SpreadingNodeData | FilterNodeData | SchemaEnforcementNodeData | BotFilteringNodeData | DataReplayNodeData | EventNodeData | TextNodeData | NotesNodeData | GroupNodeData | PlaybookNodeData;

export interface CustomNode extends Node {
  data: AnyNodeData & { nodeType?: string };
  width?: number;
  height?: number;
}

// --- Edge Customization Types ---

// Re-export XYPosition if needed elsewhere, or import directly where needed
export type { XYPosition } from 'reactflow';

// Define the possible shapes for edges, aligning with React Flow's built-in types + potential custom ones
export type EdgeShape = 'default' | 'straight' | 'step' | 'smoothstep' | 'bezier'; // 'bezier' might be the same as 'default' depending on RF version

// Define types for arrow heads
export type ArrowHeadType = 'arrow' | 'arrowclosed';

// Define a more detailed structure for edge markers (start/end arrows)
export interface EdgeMarker {
  type: ArrowHeadType;
  color?: string;
  width?: number;
  height?: number;
  strokeWidth?: number;
  orientation?: 'auto' | number; // Orientation in degrees or 'auto'
}

// Define the structure for individual control points used in editable edges
export interface ControlPointData {
  x: number;
  y: number;
  id: string; // Unique identifier for the control point
  active: boolean; // Whether the point is currently active/used in calculation
  prev?: any; // Optional previous state or related data (from example)
}

// Define the data structure for custom edge properties
export interface EdgeData {
  label?: string; // Keep existing label if needed
  status?: 'active' | 'inactive' | 'error'; // Keep existing status if needed
  shape?: EdgeShape;
  color?: string; // Overrides default style stroke
  thickness?: number; // Overrides default style strokeWidth
  animated?: boolean; // Overrides default animated prop
  markerStart?: string | EdgeMarker; // Use string for simple types (like MarkerType.ArrowClosed) or EdgeMarker object
  markerEnd?: string | EdgeMarker;   // Use string for simple types (like MarkerType.ArrowClosed) or EdgeMarker object
  // For bezier/catmull - based on the editable edge example
  points?: ControlPointData[]; // Use the defined ControlPointData type
  algorithm?: string; // e.g., 'BezierCatmullRom', 'Linear' from example
  // Allow other properties
  [key: string]: unknown;
}

// Update CustomEdge to use the new EdgeData type
export type CustomEdge = Edge<EdgeData>; // Use Edge<EdgeData> to strongly type the data property

// --- End Edge Customization Types ---


export interface ProjectData {
  id: string;
  name: string;
  description?: string;
  nodes: CustomNode[];
  edges: CustomEdge[];
  owner_id: string;
  created_at: string;
  updated_at: string;
  shared_with: string[];
  is_locked: boolean;
  is_archived: boolean;
  version: number;
  user_id?: string;
  owner?: {
    email: string;
  };
  settings?: {
    theme?: 'light' | 'dark';
    preview?: string | null;
    category?: string;
    projectScale?: 'trivial' | 'small' | 'medium' | 'large' | 'very-large';
    shareLink?: string;
  };
}

export class Project implements ProjectData {
  id: string;
  name: string;
  description?: string;
  nodes: CustomNode[];
  edges: CustomEdge[];
  owner_id: string;
  created_at: string;
  updated_at: string;
  shared_with: string[];
  is_locked: boolean;
  is_archived: boolean;
  version: number;
  user_id?: string;
  owner?: {
    email: string;
  };
  settings?: {
    theme?: 'light' | 'dark';
    preview?: string | null;
    category?: string;
    projectScale?: 'trivial' | 'small' | 'medium' | 'large' | 'very-large';
    shareLink?: string;
  };

  constructor(data: ProjectData) {
    this.id = data.id;
    this.name = data.name;
    this.description = data.description;
    this.nodes = data.nodes;
    this.edges = data.edges;
    this.owner_id = data.owner_id;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
    this.shared_with = data.shared_with;
    this.is_locked = data.is_locked;
    this.is_archived = data.is_archived;
    this.version = data.version;
    this.user_id = data.user_id;
    this.owner = data.owner;
    this.settings = data.settings;
  }

  get ownerId(): string { return this.owner_id; }
  get createdAt(): string { return this.created_at; }
  get updatedAt(): string { return this.updated_at; }
  get sharedWith(): string[] { return this.shared_with; }
  get isLocked(): boolean { return this.is_locked; }
  get isArchived(): boolean { return this.is_archived; }
  get preview(): string | null { return this.settings?.preview ?? null; }
  get category(): string | undefined { return this.settings?.category; }
  get projectScale(): string | undefined { return this.settings?.projectScale; }
  get shareLink(): string | undefined { return this.settings?.shareLink; }
}

export interface HistoryEntry {
  nodes: CustomNode[];
  edges: CustomEdge[];
  timestamp: number;
}

export interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  preview: string;
  nodes: CustomNode[];
  edges: CustomEdge[];
}

export interface ApiResponse<T> {
  data: T;
  error?: { message: string };
}

export type { Integration } from './integration';
